import { Component, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';

interface InstallationVideoCard {
  videoUrl: string;
  label: string;
  title: string;
}

interface ProductFeature {
  title: string;
  description: string;
}

interface CareGuideCard {
  title: string;
  icon: string;
  iconBackground: string;
  items: string[];
}

@Component({
  selector: 'app-wiper-intro',
  templateUrl: './wiper-intro.component.html',
  styleUrls: ['./wiper-intro.component.less']
})
export class WiperIntroComponent implements OnInit, OnDestroy {
  constructor(
    private sanitizer: DomSanitizer,
    private http: HttpClient
  ) {}
  // --- Hero ---
  readonly heroEyebrow = 'Bosch SMARTFIT Pro';
  readonly heroTitle = '博世容翼Pro雨刮片';
  readonly heroSubtitle = '百年甄选 · 卓越性能';

  // --- 安装视频 section ---
  readonly videoSectionTitle = '安装视频';
  readonly videoSectionCaption = '点击下方对应雨刮器型号查看';

  // --- 产品 section ---
  readonly productCardTitle = '博世容翼Pro雨刮片';

  // --- 养护 section ---
  readonly careSectionTitle = '想要出色的刮拭效果？';
  readonly careSectionSubtitle = '这样做！ 养护小贴士';

  // --- Heritage section ---
  readonly heritageEyebrow = 'Our Heritage';
  readonly heritageTitle = '选择博世，选择非凡';
  readonly heritageParagraphs: string[] = [
    '在汽车刚被发明的初时代，雨刮系统远没有如今智能，车主在雨天驾驶汽车的过程中，一只手需要控制方向盘，另一只手调节离合器，同时还要操作雨刮进行工作，行车安全很难得到保障。',
    '博世在1926年发明了电动雨刮系统，从而把"第三只手"从清洁车窗的无聊工作中解放了出来，雨刮也从手动时代进入了自动时代。'
  ];

  // --- Images ---
  readonly heroImage = 'assets/wiper/wiper-badge.png';
  readonly installSectionIcon = 'assets/wiper/wiper-play.png';
  readonly playButtonImage = 'assets/wiper/wiper-play-button.svg';
  readonly productPackageImage = 'assets/wiper/wiper-package.jpg';
  readonly productBladeImage = 'assets/wiper/wiper-blade.png';
  readonly heritageImage = 'assets/wiper/wiper-banner.jpg';

  readonly userCenterAdminUrl = environment.userCenterAdminUrl;

  readonly installationVideos: InstallationVideoCard[] = [
    {
      title: 'U-type',
      label: '接口1',
      videoUrl: 'https://boschaftermarket.com.cn/userCenterAdmin/file/download/video/01'
    },
    {
      title: 'Pin-type',
      label: '接口2',
      videoUrl: 'https://boschaftermarket.com.cn/userCenterAdmin/file/download/video/02'
    },
    {
      title: 'Pin-type',
      label: '接口3',
      videoUrl: 'https://boschaftermarket.com.cn/userCenterAdmin/file/download/video/03'
    },
    {
      title: 'Side-type',
      label: '接口4',
      videoUrl: 'https://boschaftermarket.com.cn/userCenterAdmin/file/download/video/04'
    },
    {
      title: 'Square-type',
      label: '接口6',
      videoUrl: 'https://boschaftermarket.com.cn/userCenterAdmin/file/download/video/06'
    },
    {
      title: 'Square-type',
      label: '接口7',
      videoUrl: 'https://boschaftermarket.com.cn/userCenterAdmin/file/download/video/07'
    },
    {
      title: 'Square-type',
      label: '接口8',
      videoUrl: 'https://boschaftermarket.com.cn/userCenterAdmin/file/download/video/08'
    },
    {
      title: 'Top-type',
      label: '接口9',
      videoUrl: 'https://boschaftermarket.com.cn/userCenterAdmin/file/download/video/09'
    },
    {
      title: 'Special-type',
      label: '接口10',
      videoUrl: 'https://boschaftermarket.com.cn/userCenterAdmin/file/download/video/10'
    },
    {
      title: 'Special-type',
      label: '接口12',
      videoUrl: 'https://boschaftermarket.com.cn/userCenterAdmin/file/download/video/12'
    },
    {
      title: 'Special-type',
      label: '接口15',
      videoUrl: 'https://boschaftermarket.com.cn/userCenterAdmin/file/download/video/15'
    }
  ];

  readonly productFeatures: ProductFeature[] = [
    {
      title: '比利时合成橡胶',
      description: '顺滑刮拭'
    },
    {
      title: '全新卡扣系统设计',
      description: '99% 车型覆盖'
    },
    {
      title: '原厂配套记忆钢条技术',
      description: '长久不变形，持久耐用'
    }
  ];

  readonly careGuideCards: CareGuideCard[] = [
    {
      title: '定期清洁挡风玻璃和雨刮胶条',
      icon: 'assets/wiper/clear-wiper-icon.svg',
      iconBackground: 'assets/wiper/wiper-bg.svg',
      items: [
        '使用湿布或海绵清除昆虫、树脂和花粉',
        '如有可能，使用专用昆虫清洁海绵（仅用于挡风玻璃，勿用于胶条）',
        '用湿棉布或类似物品清洁雨刮片',
        '驾驶前检查雨刮是否有树叶或污垢，及时清理以避免损坏'
      ]
    },
    {
      title: '为冬季准备雨刮',
      icon: 'assets/wiper/winter-wiper-icon.svg',
      iconBackground: 'assets/wiper/wiper-bg.svg',
      items: [
        '天气变冷时，在玻璃水中添加足量防冻剂',
        '夜间在雨刮下放置纸板或塑料片',
        '挡风玻璃结冰时切勿使用雨刮'
      ]
    },
    {
      title: '定期更换雨刮片',
      icon: 'assets/wiper/update-wiper-icon.svg',
      iconBackground: 'assets/wiper/wiper-bg.svg',
      items: [
        '至少每年更换一次',
        '如发现磨损或刮拭效果下降，需提前更换'
      ]
    },
    {
      title: '补充玻璃水',
      icon: 'assets/wiper/glass-water-icon.svg',
      iconBackground: 'assets/wiper/wiper-bg.svg',
      items: [
        '至少每年更换一次',
        '如发现磨损或刮拭效果下降，需提前更换'
      ]
    }
  ];

  playingSet = new Set<number>();

  activeVideoUrl: SafeResourceUrl | null = null;
  activeVideoLabel: string = '';
  isVideoLoading: boolean = false;
  hasVideoError: boolean = false;
  showVideoModal: boolean = false;
  private blobUrl: string | null = null;

  openVideoModal(video: InstallationVideoCard): void {
    this.activeVideoLabel = video.label;
    this.showVideoModal = true;
    this.isVideoLoading = true;
    this.hasVideoError = false;
    this.activeVideoUrl = null;
    this.loadVideoBlob(video.videoUrl);
  }

  private loadVideoBlob(url: string): void {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'isUserCenterAdmin': 'true'
    });

    // 从完整 URL 中提取 /userCenterAdmin 之后的路径部分
    // 例如: https://boschaftermarket.com.cn/userCenterAdmin/file/download/video/01 -> /file/download/video/01
    let apiUrl = url;
    const adminPathIndex = url.indexOf('/userCenterAdmin');
    if (adminPathIndex !== -1) {
      apiUrl = url.substring(adminPathIndex + '/userCenterAdmin'.length);
    } else {
      try {
        const urlObj = new URL(url);
        apiUrl = urlObj.pathname;
      } catch {
        // 如果不是完整 URL 则直接使用
      }
    }

    this.http.get(apiUrl, {
      headers,
      responseType: 'blob'
    }).subscribe({
      next: (blob: Blob) => {
        const videoBlob = new Blob([blob], { type: 'video/mp4' });
        this.blobUrl = URL.createObjectURL(videoBlob);
        this.activeVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
        this.isVideoLoading = false;
      },
      error: (error) => {
        console.error('Error loading video:', error);
        this.hasVideoError = true;
        this.isVideoLoading = false;
      }
    });
  }

  onVideoLoaded(): void {
    this.isVideoLoading = false;
    this.hasVideoError = false;
  }

  onVideoError(): void {
    this.hasVideoError = true;
    this.isVideoLoading = false;
  }

  closeVideoModal(): void {
    this.showVideoModal = false;
    this.activeVideoUrl = null;
    this.activeVideoLabel = '';
    this.isVideoLoading = false;
    this.hasVideoError = false;
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }
}
